import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { LOGO_WORDMARK } from "./data";
import { DropCountdown } from "./DropCountdown";

const Line = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yTitle = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollTo = (id) => {
    const el = document.querySelector(`[data-testid="${id}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[88vh] sm:min-h-screen flex flex-col justify-center overflow-hidden pt-36 pb-24 sm:pb-16"
    >
      <div className="absolute inset-0 hero-radial" />
      <motion.img
        src={LOGO_WORDMARK}
        alt=""
        aria-hidden
        style={{ y: yLogo }}
        className="pointer-events-none absolute right-[-8%] top-[8%] w-[55vw] max-w-[640px] opacity-[0.035] hidden md:block"
      />

      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 w-full"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="flex items-center gap-3 mb-7"
        >
          <span className="diamond" />
          <p className="font-mono-d text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[#ff2a85]">
            Deen District Presents — Drop 001
          </p>
        </motion.div>

        <motion.div style={{ y: yTitle }}>
          <h1 className="font-display font-extrabold leading-[0.9] tracking-tight uppercase">
            <Line delay={0.35} className="text-[13.5vw] lg:text-[11rem]">
              The
            </Line>
            <Line delay={0.5} className="text-pink text-[13.5vw] lg:text-[11rem]">
              Origin
            </Line>
            <Line
              delay={0.65}
              className="text-stroke text-[7.2vw] lg:text-[7rem] whitespace-nowrap"
            >
              Collection
            </Line>
          </h1>
        </motion.div>

        <div className="mt-9 grid lg:grid-cols-[1fr_auto] gap-8 lg:items-end">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-xl"
          >
            <p className="font-serif-accent text-xl sm:text-2xl text-[#0a0a0b] leading-snug">
              “Before the Movement, There was Faith.”
            </p>
            <p className="mt-4 text-sm sm:text-base text-[#5f5e5a] leading-relaxed">
              Luxury streetwear meets unapologetic Muslim identity. Built on
              Deen. Raised by Culture. Worn with Purpose.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <button
              data-testid="hero-join-button"
              onClick={() => scrollTo("email-signup-section")}
              className="px-8 py-4 rounded-full bg-[#ff2a85] text-white font-mono-d text-[11px] uppercase tracking-[0.2em] font-medium hover:bg-[#e60067] hover:shadow-[0_10px_40px_-8px_rgba(255,42,133,0.65)] transition-all duration-300"
            >
              Pre-Order Drop 001
            </button>
            <button
              data-testid="hero-lookbook-button"
              onClick={() => scrollTo("product-showcase-section")}
              className="px-8 py-4 rounded-full border border-[#e3e2dc] text-[#0a0a0b] font-mono-d text-[11px] uppercase tracking-[0.2em] font-medium hover:border-[#0a0a0b]/60 transition-all duration-300"
            >
              Shop The Drop
            </button>
          </motion.div>
        </div>

        <div className="mt-10">
          <DropCountdown />
        </div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("marquee-section")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#5f5e5a] hover:text-[#ff2a85] transition-colors"
        aria-label="Scroll down"
      >
        <span className="font-mono-d text-[10px] uppercase tracking-[0.3em]">
          Pre-Order Now
        </span>
        <motion.span
          animate={{ y: [0, 7, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
};
